import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import useStores from 'shared/hooks/use-stores';
import { getProductWeight } from 'shared/helpers/products';
import { ApolloError } from 'apollo-client';
import { useCheckAllotmentQuery, GqlAllotmentInfo, GqlCartItemInput, AllotmentStatus } from 'types/graphql';

// Mirrors the MobX CartItem class shape (state/cart-item.js) used by mapInputsForPriceCartAPI.
// TODO: Remove once CartItem and CartStore are typed.
type CartItem = {
  product: {
    POSMetaData: {
      canonicalID?: string;
      children?: Array<{ option: string; canonicalID?: string; packageQuantity?: number }>;
    };
  };
  option: string;
  key: string;
  quantity: number;
  basePrice: number;
};

type AllotmentCheckResult = {
  allotmentInfo: GqlAllotmentInfo | null;
  loading: boolean;
  error: ApolloError | undefined;
  isAllotmentExceeded: boolean;
  shouldShowAllotment: boolean;
};

function mapCartItemsForAllotment(cart: Record<string, CartItem>): GqlCartItemInput[] {
  return Object.values(cart).reduce<GqlCartItemInput[]>((cartItems, item) => {
    const children = item.product.POSMetaData.children ?? [];
    const childProduct = children.find((child) => child.option === item.option);
    const productId = Number(childProduct?.canonicalID ?? item.product.POSMetaData.canonicalID);

    if (!productId) {
      return cartItems;
    }

    cartItems.push({
      productId,
      ecommKey: item.key,
      quantity: childProduct?.packageQuantity ? childProduct.packageQuantity * item.quantity : item.quantity,
      unitPrice: item.basePrice,
      unitWeightGrams: getProductWeight(item.option, 0, 1) / (childProduct?.packageQuantity ?? 1),
    });

    return cartItems;
  }, []);
}

// TODO: Remove dev mock once the Armageddon allotment endpoint is live.
// Flip to `true` to bypass the network call and render fake allotment data locally.
// Change `status` to test different states (exceeded, withinLimits, error).
const DEV_MOCK_ENABLED = false;
const DEV_MOCK_ALLOTMENT: GqlAllotmentInfo = {
  __typename: 'AllotmentInfo',
  status: AllotmentStatus.exceeded,
  details: [
    {
      __typename: 'AllotmentDetail',
      category: 'Smoking Marijuana',
      limit: 2.5,
      used: 2.3,
      status: AllotmentStatus.exceeded,
      unit: 'oz',
    },
    {
      __typename: 'AllotmentDetail',
      category: 'Medical Marijuana',
      limit: 24500,
      used: 4500,
      status: AllotmentStatus.withinLimits,
      unit: 'mg',
    },
    {
      __typename: 'AllotmentDetail',
      category: 'Low-THC Cannabis',
      limit: 28000,
      used: 3500,
      status: AllotmentStatus.withinLimits,
      unit: 'mg',
    },
  ],
  error: null,
};

export default function useAllotmentCheck(): AllotmentCheckResult {
  const { Cart } = useStores();
  const flags = useFlags();

  const { order, dispensary } = Cart;
  const isFloridaMedical = dispensary?.location?.state === 'FL' && order.medicalOrder;
  const floridaAllotmentCheckEnabled = flags['ecomm.fl-mmur-allotment-check.rollout'] ?? false;
  const shouldCheck = isFloridaMedical && floridaAllotmentCheckEnabled;

  const cartItems = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!shouldCheck || DEV_MOCK_ENABLED) {
      return [];
    }
    return mapCartItemsForAllotment(order.cart);
  }, [shouldCheck, order.cart]);

  const { data, loading, error } = useCheckAllotmentQuery({
    variables: {
      input: {
        dispensaryId: dispensary?.id,
        cart: cartItems,
      },
    },
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    skip: !shouldCheck || DEV_MOCK_ENABLED || cartItems.length === 0,
    fetchPolicy: 'network-only',
  });

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const allotmentInfo = DEV_MOCK_ENABLED && shouldCheck ? DEV_MOCK_ALLOTMENT : data?.checkAllotment ?? null;
  const isAllotmentExceeded = allotmentInfo?.status === AllotmentStatus.exceeded;

  return {
    allotmentInfo,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    loading: shouldCheck && !DEV_MOCK_ENABLED && loading,
    error,
    isAllotmentExceeded,
    shouldShowAllotment: shouldCheck,
  };
}
